import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import { Business as BusinessIcon, Cloud as CloudIcon } from '@mui/icons-material';
import { useCustomer, Customer, customerKey } from '../../contexts/CustomerContext';
import { useAuthContext } from '../../Auth';

interface CustomerSelectionProps {
  open: boolean;
  onClose?: () => void;
  mode: 'first-login' | 'returning' | 'switch';
  lastSelectedCustomer?: Customer | null;
}

interface TenantGroup {
  tenant: string;
  customer_name: string;
  environments: string[];
}

const CustomerSelection: React.FC<CustomerSelectionProps> = ({
  open,
  onClose,
  mode,
  lastSelectedCustomer,
}) => {
  const { tokenData } = useAuthContext();
  const { availableCustomers, setCustomer, loadCustomers, isLoading } = useCustomer();

  const [step, setStep] = useState<'tenant' | 'environment'>('tenant');
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
  const [selectedEnvironment, setSelectedEnvironment] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [localLoading, setLocalLoading] = useState(false);

  // Group the flat (tenant, environment, customer_name) list into tenants + their envs.
  const tenants: TenantGroup[] = useMemo(() => {
    const byTenant = new Map<string, TenantGroup>();
    for (const c of availableCustomers) {
      const existing = byTenant.get(c.tenant);
      if (existing) {
        if (!existing.environments.includes(c.environment)) {
          existing.environments.push(c.environment);
        }
      } else {
        byTenant.set(c.tenant, {
          tenant: c.tenant,
          customer_name: c.customer_name,
          environments: [c.environment],
        });
      }
    }
    for (const g of byTenant.values()) g.environments.sort();
    return Array.from(byTenant.values()).sort((a, b) =>
      a.customer_name.localeCompare(b.customer_name)
    );
  }, [availableCustomers]);

  const selectedTenantGroup = useMemo(
    () => tenants.find((t) => t.tenant === selectedTenant) || null,
    [tenants, selectedTenant]
  );

  useEffect(() => {
    if (open) {
      loadCustomers().catch(() => setError('Failed to load customers'));
    }
  }, [open]);

  // Preselect the returning user's last tenant + env so the wizard opens on
  // the environment step with their last choice highlighted.
  useEffect(() => {
    if (mode === 'returning' && lastSelectedCustomer) {
      setSelectedTenant(lastSelectedCustomer.tenant);
      setSelectedEnvironment(lastSelectedCustomer.environment);
    }
  }, [mode, lastSelectedCustomer]);

  const resetWizard = () => {
    setStep('tenant');
    setSelectedTenant(null);
    setSelectedEnvironment(null);
    setError(null);
  };

  const commit = async (tenant: string, environment: string) => {
    try {
      setLocalLoading(true);
      await setCustomer(tenant, environment, tokenData?.email);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to select customer');
      setLocalLoading(false);
    }
  };

  const handleTenantSelect = (tenant: string) => {
    setSelectedTenant(tenant);
    setSelectedEnvironment(null);
    setError(null);
    const group = tenants.find((t) => t.tenant === tenant);
    // Auto-advance + auto-select when there's only one environment. Users
    // stay on step 1 conceptually — one click, one selection.
    if (group && group.environments.length === 1) {
      setSelectedEnvironment(group.environments[0]);
      setStep('environment');
    } else {
      setStep('environment');
    }
  };

  const handleContinueWithLast = async () => {
    if (!lastSelectedCustomer) return;
    await commit(lastSelectedCustomer.tenant, lastSelectedCustomer.environment);
  };

  const handleConfirm = async () => {
    if (!selectedTenant || !selectedEnvironment) {
      setError('Please select an environment');
      return;
    }
    await commit(selectedTenant, selectedEnvironment);
  };

  const dialogTitle = (() => {
    if (mode === 'returning') return 'Welcome Back!';
    if (mode === 'switch') return 'Switch Customer';
    return 'Welcome! Select Your Customer';
  })();

  const stepLabel = step === 'tenant' ? 'Step 1 of 2 — Choose a customer' : 'Step 2 of 2 — Choose an environment';

  const renderTenantStep = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {stepLabel}
      </Typography>
      <List sx={{ maxHeight: 400, overflow: 'auto' }}>
        {tenants.map((t) => (
          <ListItem key={t.tenant} disablePadding>
            <ListItemButton
              selected={selectedTenant === t.tenant}
              onClick={() => handleTenantSelect(t.tenant)}
            >
              <ListItemIcon>
                <BusinessIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={t.customer_name}
                secondary={
                  <Stack direction="row" spacing={0.5} alignItems="center" component="span">
                    <span>Tenant: {t.tenant}</span>
                    <span>·</span>
                    <span>
                      {t.environments.length} environment{t.environments.length === 1 ? '' : 's'}
                    </span>
                  </Stack>
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const renderEnvironmentStep = () => {
    if (!selectedTenantGroup) return null;
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {stepLabel}
        </Typography>
        <Box
          sx={{
            p: 2,
            mb: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <BusinessIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1">{selectedTenantGroup.customer_name}</Typography>
            <Chip size="small" label={selectedTenantGroup.tenant} variant="outlined" />
          </Stack>
        </Box>
        <List sx={{ maxHeight: 300, overflow: 'auto' }}>
          {selectedTenantGroup.environments.map((env) => (
            <ListItem key={env} disablePadding>
              <ListItemButton
                selected={selectedEnvironment === env}
                onClick={() => {
                  setSelectedEnvironment(env);
                  setError(null);
                }}
              >
                <ListItemIcon>
                  <CloudIcon color="action" />
                </ListItemIcon>
                <ListItemText primary={env} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };

  const renderReturningIntro = () => {
    if (!lastSelectedCustomer) return null;
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Continue with your last selection?
        </Typography>
        <Box
          sx={{
            p: 2,
            mb: 2,
            border: '2px solid',
            borderColor: 'primary.main',
            borderRadius: 1,
            bgcolor: 'primary.50',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <BusinessIcon color="primary" />
            <Typography variant="h6">{lastSelectedCustomer.customer_name}</Typography>
            <Chip size="small" label={lastSelectedCustomer.environment} color="primary" />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {customerKey(lastSelectedCustomer)}
          </Typography>
        </Box>
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={handleContinueWithLast}
          disabled={localLoading}
        >
          Continue with {lastSelectedCustomer.customer_name} / {lastSelectedCustomer.environment}
        </Button>
        <Divider sx={{ my: 2 }}>
          <Typography variant="body2" color="text.secondary">
            or pick a different customer
          </Typography>
        </Divider>
      </Box>
    );
  };

  const renderBody = () => {
    if (isLoading || localLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    return (
      <>
        {mode === 'returning' && renderReturningIntro()}
        {step === 'tenant' ? renderTenantStep() : renderEnvironmentStep()}
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={mode === 'switch' ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={mode !== 'switch'}
    >
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {renderBody()}
      </DialogContent>
      <DialogActions>
        {mode === 'switch' && onClose && (
          <Button onClick={onClose} disabled={localLoading}>
            Cancel
          </Button>
        )}
        {step === 'environment' && !localLoading && (
          <Button onClick={resetWizard} disabled={localLoading}>
            Back
          </Button>
        )}
        {step === 'environment' && (
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!selectedEnvironment || localLoading}
          >
            {localLoading ? <CircularProgress size={24} /> : 'Confirm'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CustomerSelection;
